// frontend/src/ts/protocols/common/hooks/useUnifiedFiles.ts
import { useMemo } from 'react'
import { IProtocolRawFile } from '@/protocols/types/IProtocolRawFile'
import { extractOriginalFilename } from '@/common/utils/fileUtils'
import { TProtocolId } from '@/protocols/types/protocol'
import { useUploadStatusStore } from '@/protocols/persistence/useUploadStatusStore'

export interface UnifiedFileEntry {
  id: string
  kind: 'queued' | 'uploading' | 'uploaded' | 'pending_visibility' | 'failed'
  filename: string
  size: number | null
  progress?: number
  pendingUploadId?: number
  serverFile?: IProtocolRawFile
  /** Failed browser uploads: error text + tempId for retry/dismiss. */
  error?: string
  browserTempId?: number
}

interface UseUnifiedFilesArgs {
  protocolId: TProtocolId
  uploadedFiles: IProtocolRawFile[]
}

export function useUnifiedFiles(args: UseUnifiedFilesArgs): UnifiedFileEntry[] {
  const { protocolId, uploadedFiles } = args
  const pendingUploads = useUploadStatusStore((s) => s.pendingUploads)
  const browserUploads = useUploadStatusStore((s) => s.browserUploads)

  return useMemo(() => {
    const entries: UnifiedFileEntry[] = []
    const mirroredBrowserUploadIds = new Set<number>()

    browserUploads.forEach((entry, tempId) => {
      if (entry.status === 'in_flight' || entry.status === 'dead') {
        mirroredBrowserUploadIds.add(tempId)
      }
    })

    // 1. Pending uploads (Rust queue — covers files enqueued by the
    //    watcher, by the native picker, or by the native drag/drop).
    pendingUploads.forEach((row) => {
      // Browser-mode uploads are already mirrored into pendingUploads by
      // useUploadStatusStore.browserUploadRows(). Skip those mirrored rows
      // here and let the direct browserUploads pass below render the single
      // authoritative entry so the file table doesn't show duplicates.
      if (mirroredBrowserUploadIds.has(row.id)) return

      if (row.state === 'found' || row.state === 'copied' || row.state === 'pending') {
        entries.push({
          id: `pending-${row.id}`,
          kind: 'queued',
          filename: row.filename,
          size: row.fileSize,
          pendingUploadId: row.id,
        })
      } else if (row.state === 'uploading') {
        entries.push({
          id: `pending-${row.id}`,
          kind: 'uploading',
          filename: row.filename,
          size: row.fileSize,
          progress: row.uploadProgress,
          pendingUploadId: row.id,
        })
      }
    })

    // 2. Browser uploads (in-flight XHR PUT to S3). In Tauri mode this
    //    slice is empty — Tauri drag/drop and picker both go straight
    //    to enqueue_picked_file so the file lives in pendingUploads.
    browserUploads.forEach((entry) => {
      if (entry.status === 'in_flight') {
        entries.push({
          id: `browser-${entry.tempId}`,
          kind: 'uploading',
          filename: entry.filename,
          size: entry.fileSize,
          progress: entry.progress,
        })
      } else if (entry.status === 'dead') {
        // Failed uploads stay visible (with a Retry affordance in
        // FileActionsCell) instead of vanishing — the File is retained
        // so the user doesn't have to re-pick it.
        entries.push({
          id: `browser-${entry.tempId}`,
          kind: 'failed',
          filename: entry.filename,
          size: entry.fileSize,
          error: entry.error,
          browserTempId: entry.tempId,
        })
      }
      // 'persisted' is intentionally skipped — the file surfaces via
      // loadProtocolFiles within ~1s after this state. 'cancelled' is
      // intentionally skipped — a cancelled upload should disappear
      // cleanly without leaving a stale row.
    })

    // 3. Uploaded files (server side) — but only those NOT already
    //    in the pending uploads list (a row that's persisted but
    //    not yet swept from the queue shows up as 'pending_visibility')
    const pendingFilenames = new Set(pendingUploads.map((r) => r.filename))
    uploadedFiles.forEach((file) => {
      const original = extractOriginalFilename(file.id, file.filename)
      if (pendingFilenames.has(original)) {
        entries.push({
          id: `uploaded-${file.id}`,
          kind: 'pending_visibility',
          filename: original,
          size: file.size ?? null,
          serverFile: file,
        })
      } else {
        entries.push({
          id: `uploaded-${file.id}`,
          kind: 'uploaded',
          filename: original,
          size: file.size ?? null,
          serverFile: file,
        })
      }
    })

    // protocolId is kept in the dependency array so the memo
    // invalidates when the protocol context changes.
    void protocolId

    entries.sort((a, b) => a.id.localeCompare(b.id))

    return entries
  }, [pendingUploads, uploadedFiles, browserUploads, protocolId])
}